/**
 * CategorySelector Component
 *
 * Two-dropdown selector for category and subcategory.
 * Subcategory options update based on ticket type and selected category.
 */

import { useMemo } from 'react';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  TicketType,
  type IncidentCategory,
  type IncidentSubcategory,
  type RequestCategory,
  type RequestSubcategory,
  getCategoriesForTicketType,
  getSubcategoriesForCategory,
} from '../../types/ticket';
import { AppSelect } from '../ui/AppSelect';
import './CategorySelector.css';

type CategoryValue = IncidentCategory | RequestCategory | '';
type SubcategoryValue = IncidentSubcategory | RequestSubcategory | '';

interface CategorySelectorProps {
  ticketType: TicketType | '';
  category: CategoryValue;
  subcategory: SubcategoryValue;
  onCategoryChange: (category: CategoryValue) => void;
  onSubcategoryChange: (subcategory: SubcategoryValue) => void;
  categoryError?: string;
  subcategoryError?: string;
  disabled?: boolean;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  ticketType,
  category,
  subcategory,
  onCategoryChange,
  onSubcategoryChange,
  categoryError,
  subcategoryError,
  disabled = false,
}) => {
  // Get categories based on ticket type
  const categoryOptions = useMemo(() => {
    if (!ticketType) return [];
    return getCategoriesForTicketType(ticketType as TicketType).map((cat) => ({
      value: cat,
      label: cat,
    }));
  }, [ticketType]);

  // Get subcategories based on category
  const subcategoryOptions = useMemo(() => {
    if (!ticketType || !category) return [];
    return getSubcategoriesForCategory(
      ticketType as TicketType,
      category as IncidentCategory | RequestCategory
    ).map((sub) => ({
      value: sub,
      label: sub,
    }));
  }, [ticketType, category]);

  const handleCategoryChange = (event: SelectChangeEvent<unknown>) => {
    const newCategory = event.target.value as CategoryValue;
    onCategoryChange(newCategory);
    // Clear subcategory when category changes
    onSubcategoryChange('');
  };

  const handleSubcategoryChange = (event: SelectChangeEvent<unknown>) => {
    onSubcategoryChange(event.target.value as SubcategoryValue);
  };

  const isCategoryDisabled = disabled || !ticketType;
  const isSubcategoryDisabled = disabled || !category;

  return (
    <div className="category-selector">
      <div className="category-selector__row">
        <div className="category-selector__field">
          <AppSelect
            label="Category"
            value={category}
            onChange={handleCategoryChange}
            options={categoryOptions}
            placeholder="Select category"
            error={!!categoryError}
            errorText={categoryError}
            disabled={isCategoryDisabled}
            required
          />
          {!ticketType && !disabled && (
            <span className="category-selector__hint">
              Select a ticket type first
            </span>
          )}
        </div>

        <div className="category-selector__field">
          <AppSelect
            label="Subcategory"
            value={subcategory}
            onChange={handleSubcategoryChange}
            options={subcategoryOptions}
            placeholder="Select subcategory"
            error={!!subcategoryError}
            errorText={subcategoryError}
            disabled={isSubcategoryDisabled}
            required
          />
          {ticketType && !category && !disabled && (
            <span className="category-selector__hint">
              Select a category first
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
